import axios from 'axios';
import config from '../config';

export const createSchoolOrder = async (formData) => {
  try {
    
    // Get the school name from the organization
    let schoolName = formData.schoolName || '';
    if (!schoolName && formData.organization_id) {
      // Try to get the name from localStorage if available
      const organizations = JSON.parse(localStorage.getItem('organizations') || '[]');
      const selectedOrg = organizations.find(org => org.id === formData.organization_id);
      if (selectedOrg) {
        schoolName = selectedOrg.name;
      }
    }
    
    // Ensure organizationId is set
    const organizationId = formData.organizationId || formData.organization_id;
    if (!organizationId) {
      throw new Error('Organisation ID is required for school orders');
    }
    
    // Create contactPerson from firstName and lastName if not explicitly provided
    const firstName = formData.firstName || '';
    const lastName = formData.lastName || '';
    const contactPerson = formData.contactPerson || 
                         (firstName && lastName ? `${firstName} ${lastName}` : 
                          firstName ? firstName : 
                          lastName ? lastName : 'Contact Person');
    
    // Convert boolean product values to integers for the server
    const hasSchoolLink = formData.productsOrdered?.schoolLink ? 1 : 0;
    const hasEPublisher = formData.productsOrdered?.ePublisher ? 1 : 0;
    const hasPaper = formData.productsOrdered?.paper ? 1 : 0;
    
    // For backwards compatibility, still create a productsOrdered string from checkboxes
    const productsArray = [];
    if (formData.productsOrdered?.schoolLink) productsArray.push('SchoolLink');
    if (formData.productsOrdered?.ePublisher) productsArray.push('ePublisher');
    if (formData.productsOrdered?.paper) productsArray.push('Paper');
    
    // Get the payment model and ensure it's formatted correctly
    let paymentModelValue = formData.paymentModel || 'Ad Supported';
    
    // Make sure we're using the proper format that matches the database
    if (paymentModelValue === 'adSupported') {
      paymentModelValue = 'Ad Supported';
    } else if (paymentModelValue === 'paid') {
      paymentModelValue = 'Paid';
    }
    
    // Calculate endDate if not provided
    let endDate = formData.endDate;
    if (!endDate) {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      endDate = oneYearFromNow.toISOString().split('T')[0];
    }
    
    const data = {
      orderType: 'school',
      organizationId: organizationId,
      
      // Contact information fields
      firstName: firstName,
      lastName: lastName,
      contactPerson: contactPerson,
      position: formData.position || '',
      email: formData.email || '',
      phone: formData.phone || '',
      
      // Primary contact fields
      primaryName: formData.primary_name || '',
      primaryEmail: formData.primary_email || '',
      primaryPhone: formData.primary_phone || '',
      
      // Accounts contact fields
      accountsName: formData.accounts_name || '',
      accountsEmail: formData.accounts_email || '',
      accountsPhone: formData.accounts_phone || '',
      
      // Principal contact fields
      principalName: formData.principal_name || '',
      principalEmail: formData.principal_email || '',
      principalPhone: formData.principal_phone || '',
      
      // Business Manager contact fields
      businessManagerName: formData.business_manager_name || '',
      businessManagerEmail: formData.business_manager_email || '',
      businessManagerPhone: formData.business_manager_phone || '',
      
      // Other school order fields
      numberOfFamilies: formData.numberOfFamilies || 0,
      issuesPerTerm: formData.issuesPerTerm || 0,
      paymentModel: paymentModelValue,
      schoolName: schoolName,
      endDate: endDate,
      totalPrice: parseFloat(formData.totalPrice) || 0.00,
      
      // Both old and new product formats for compatibility
      productsOrdered: productsArray.join(',') || '',
      hasSchoolLink,
      hasEPublisher,
      hasPaper,
      
      // Notes fields
      notes: formData.generalNotes || '',
      designNotes: formData.designNotes || ''
    };
    
    const response = await axios.post(`${config.apiUrl}/api/school-orders`, data, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    // Log the complete response for debugging
    
    // Check if the server indicated issues with organization updates
    // Normalize the response structure regardless of how the server formats it
    let normalizedResponse = {
      success: false,
      message: '',
      data: null,
      orgUpdateSuccess: true,  // Default to true
      orgUpdateError: null     // Default to null
    };
    
    if (response.data) {
      normalizedResponse.success = !!response.data.success;
      normalizedResponse.message = response.data.message || '';
      
      // Extract data from nested structure if available
      if (response.data.data) {
        normalizedResponse.data = response.data.data;
        
        // Extract organization update status and error info
        if (response.data.data.hasOwnProperty('orgUpdateSuccess')) {
          normalizedResponse.orgUpdateSuccess = !!response.data.data.orgUpdateSuccess;
          normalizedResponse.orgUpdateError = response.data.data.orgUpdateError;
        }
      }
    }
    
    if (normalizedResponse.success) {
      if (!normalizedResponse.orgUpdateSuccess) {
        console.warn('School order created but organization update may be pending:', 
                    normalizedResponse.orgUpdateError);
      } else {
      }
      
      // Return normalized response regardless of server format
      return normalizedResponse;
    } else {
      throw new Error(normalizedResponse.message || 'Unknown error creating school order');
    }
  } catch (error) {
    console.error('Error creating school order:', error);
    
    // Enhanced error reporting
    if (error.response) {
      console.error('Server response:', error.response.data);
      console.error('Status code:', error.response.status);
      
      // Use the server's error message if available
      if (error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      }
    }
    
    throw error;
  }
}; 